/**
 * COMPONENTE: Historical vs Projected Chart V2
 * 
 * ✨ MELHORIAS V2.0:
 * 1. ✅ Linha tracejada para projeções
 * 2. ✅ Múltiplos cenários (Pessimista, Base, Otimista)
 * 3. ✅ Área sombreada entre min/max
 * 4. ✅ Animações de transição
 * 5. ✅ Exportação (PNG, CSV)
 * 6. ✅ Dados históricos do DRE
 * 
 * @version 2.0.0
 * @updated 2025-10-21
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
  Chip,
  IconButton,
  Tooltip as MuiTooltip,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ComposedChart,
  Area,
} from 'recharts';
import { 
  TrendingUp, 
  BarChart, 
  ShowChart,
  Download,
  FileDownload,
  Image as ImageIcon,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';

// ============================================================================
// TIPOS
// ============================================================================

type MetricType = 'receita' | 'ebitda' | 'fcff';

export interface ChartDataPoint {
  year: number;
  yearLabel: string;
  receita: number;
  ebitda: number;
  fcff: number;
  isProjection: boolean;
  // Dados por cenário (para múltiplos cenários)
  receita_pessimista?: number;
  receita_base?: number;
  receita_otimista?: number;
  ebitda_pessimista?: number;
  ebitda_base?: number;
  ebitda_otimista?: number;
  fcff_pessimista?: number;
  fcff_base?: number;
  fcff_otimista?: number;
}

export interface HistoricalVsProjectedChartProps {
  /** Dados históricos e projetados */
  data: ChartDataPoint[];
  /** Métrica inicial selecionada */
  defaultMetric?: MetricType;
  /** Título do componente */
  title?: string;
  /** Altura do gráfico (px) */
  height?: number;
  /** Habilitar múltiplos cenários */
  enableMultipleScenarios?: boolean;
}

// ============================================================================
// CONFIGURAÇÕES
// ============================================================================

const METRIC_CONFIG = {
  receita: {
    label: 'Receita Líquida',
    color: '#1976d2', // Azul
    icon: <TrendingUp />,
    format: (value: number) => `R$ ${(value / 1000).toFixed(0)}k`,
  },
  ebitda: {
    label: 'EBITDA',
    color: '#2e7d32', // Verde
    icon: <BarChart />,
    format: (value: number) => `R$ ${(value / 1000).toFixed(0)}k`,
  },
  fcff: {
    label: 'FCFF',
    color: '#ed6c02', // Laranja
    icon: <ShowChart />,
    format: (value: number) => `R$ ${(value / 1000).toFixed(0)}k`,
  },
};

const SCENARIO_CONFIG = {
  pessimista: {
    label: 'Pessimista',
    color: '#f44336', // Vermelho
    strokeDasharray: '8 4',
  },
  base: {
    label: 'Base',
    color: '#2196f3', // Azul
    strokeDasharray: '0', // Sólido
  },
  otimista: {
    label: 'Otimista',
    color: '#4caf50', // Verde
    strokeDasharray: '4 2',
  },
};

// ============================================================================
// FUNÇÕES DE EXPORTAÇÃO
// ============================================================================

/**
 * Exportar gráfico como PNG
 */
async function exportToPNG(chartRef: HTMLElement, filename: string = 'grafico.png') {
  try {
    const canvas = await html2canvas(chartRef, {
      backgroundColor: '#161D1C', // Background MD3 dark (surfaceContainerLow)
      scale: 2, // Maior qualidade
    });
    
    const link = document.createElement('a');
    link.download = filename;
    link.href = canvas.toDataURL('image/png');
    link.click();
  } catch (error) {
    console.error('[Export] Erro ao exportar PNG:', error);
    alert('Erro ao exportar imagem. Tente novamente.');
  }
}

/**
 * Exportar dados como CSV
 */
function exportToCSV(data: ChartDataPoint[], metric: MetricType, filename: string = 'dados.csv') {
  try {
    // Cabeçalho CSV
    let csv = 'Ano,Tipo,Valor\n';
    
    // Linhas de dados
    data.forEach((point) => {
      const tipo = point.isProjection ? 'Projeção' : 'Histórico';
      const valor = point[metric];
      csv += `${point.yearLabel},${tipo},${valor}\n`;
    });
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  } catch (error) {
    console.error('[Export] Erro ao exportar CSV:', error);
    alert('Erro ao exportar CSV. Tente novamente.');
  }
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function HistoricalVsProjectedChartV2({
  data,
  defaultMetric = 'receita',
  title = 'Histórico vs Projeção',
  height = 400,
  enableMultipleScenarios = false,
}: HistoricalVsProjectedChartProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartRef = useRef<HTMLDivElement>(null);
  
  // Estados
  const [selectedMetric, setSelectedMetric] = useState<MetricType>(defaultMetric);
  const [showMultipleScenarios, setShowMultipleScenarios] = useState(false);
  const [showConfidenceArea, setShowConfidenceArea] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // Configuração da métrica atual
  const config = METRIC_CONFIG[selectedMetric];
  
  // Encontrar ponto de divisão
  const firstProjection = data.find((d) => d.isProjection);
  const lastHistoricalYear = firstProjection ? firstProjection.year - 1 : 2024;
  
  // Separar dados
  const historicalData = data.filter((d) => !d.isProjection);
  const projectedData = data.filter((d) => d.isProjection);
  
  // Calcular anos dinamicamente para as legendas
  const firstHistoricalYear = historicalData[0]?.year || 2020;
  const firstProjectionYear = firstProjection?.year || 2025;
  const lastProjectionYear = projectedData[projectedData.length - 1]?.year || 2029;

  // ========================================================================
  // HANDLERS
  // ========================================================================

  const handleMetricChange = (_event: React.MouseEvent<HTMLElement>, newMetric: MetricType | null) => {
    if (newMetric !== null) {
      setSelectedMetric(newMetric);
    }
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExportPNG = () => {
    if (chartRef.current) {
      const filename = `${title.replace(/\s+/g, '_')}_${selectedMetric}_${new Date().toISOString().split('T')[0]}.png`;
      exportToPNG(chartRef.current, filename);
    }
    handleExportMenuClose();
  };

  const handleExportCSV = () => {
    const filename = `${title.replace(/\s+/g, '_')}_${selectedMetric}_${new Date().toISOString().split('T')[0]}.csv`;
    exportToCSV(data, selectedMetric, filename);
    handleExportMenuClose();
  };

  // ========================================================================
  // TOOLTIP CUSTOMIZADO
  // ========================================================================

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: ChartDataPoint }> }) => {
    if (active && payload && payload.length > 0) {
      const dataPoint = payload[0].payload;
      const isProjected = dataPoint.isProjection;

      return (
        <Card
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.98)',
            border: `2px solid ${config.color}`,
            boxShadow: 3,
            minWidth: 200,
          }}
        >
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: '#1a1a1a' }}>
              {dataPoint.yearLabel}
              {isProjected && (
                <Chip
                  label="Projeção"
                  size="small"
                  color="primary"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Typography>

            {/* Valor principal */}
            <Typography variant="body2" sx={{ color: '#333', mb: 1 }}>
              <strong>{config.label}:</strong> {config.format(dataPoint[selectedMetric])}
            </Typography>

            {/* Múltiplos cenários (se habilitado e for projeção) */}
            {showMultipleScenarios && isProjected && (
              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Cenários:
                </Typography>
                {Object.entries(SCENARIO_CONFIG).map(([key, scenarioConfig]) => {
                  const value = dataPoint[`${selectedMetric}_${key}` as keyof ChartDataPoint];
                  if (value === undefined) return null;
                  return (
                    <Typography key={key} variant="caption" sx={{ display: 'block', color: scenarioConfig.color }}>
                      <strong>{scenarioConfig.label}:</strong> {config.format(value as number)}
                    </Typography>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      );
    }
    return null;
  };

  // ========================================================================
  // RENDERIZAÇÃO
  // ========================================================================

  return (
    <Card
      ref={chartRef}
      elevation={2}
      data-testid="historical-vs-projected-chart-v2"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* ============================================================
            CABEÇALHO
        ============================================================ */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>

          {/* Controles */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Toggle de Métricas */}
            <ToggleButtonGroup
              value={selectedMetric}
              exclusive
              onChange={handleMetricChange}
              size="small"
              aria-label="métrica selecionada"
            >
              <ToggleButton value="receita" aria-label="receita">
                {!isMobile && METRIC_CONFIG.receita.icon}
                <Typography variant="caption" sx={{ ml: isMobile ? 0 : 0.5 }}>
                  {isMobile ? 'Rec' : 'Receita'}
                </Typography>
              </ToggleButton>
              <ToggleButton value="ebitda" aria-label="ebitda">
                {!isMobile && METRIC_CONFIG.ebitda.icon}
                <Typography variant="caption" sx={{ ml: isMobile ? 0 : 0.5 }}>
                  EBITDA
                </Typography>
              </ToggleButton>
              <ToggleButton value="fcff" aria-label="fcff">
                {!isMobile && METRIC_CONFIG.fcff.icon}
                <Typography variant="caption" sx={{ ml: isMobile ? 0 : 0.5 }}>
                  FCFF
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Botão Exportar */}
            {!isMobile && (
              <>
                <MuiTooltip title="Exportar">
                  <IconButton size="small" onClick={handleExportMenuOpen}>
                    <Download />
                  </IconButton>
                </MuiTooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleExportMenuClose}
                >
                  <MenuItem onClick={handleExportPNG}>
                    <ImageIcon sx={{ mr: 1 }} fontSize="small" />
                    Exportar como PNG
                  </MenuItem>
                  <MenuItem onClick={handleExportCSV}>
                    <FileDownload sx={{ mr: 1 }} fontSize="small" />
                    Exportar como CSV
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>

        {/* ============================================================
            CONTROLES AVANÇADOS
        ============================================================ */}
        {enableMultipleScenarios && (
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showMultipleScenarios}
                  onChange={(e) => setShowMultipleScenarios(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Mostrar Cenários</Typography>}
            />
            {showMultipleScenarios && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showConfidenceArea}
                    onChange={(e) => setShowConfidenceArea(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="caption">Área de Confiança</Typography>}
              />
            )}
          </Box>
        )}

        {/* ============================================================
            LEGENDA
        ============================================================ */}
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            mb: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* Histórico */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 3,
                bgcolor: config.color,
                borderRadius: 1,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Histórico ({firstHistoricalYear}-{lastHistoricalYear})
            </Typography>
          </Box>

          {/* Projeção */}
          {!showMultipleScenarios ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 3,
                  borderTop: `3px dashed ${config.color}`,
                  borderRadius: 1,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Projeção ({firstProjectionYear}-{lastProjectionYear})
              </Typography>
            </Box>
          ) : (
            // Legendas por cenário
            Object.entries(SCENARIO_CONFIG).map(([key, scenarioConfig]) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 3,
                    borderTop: `3px dashed ${scenarioConfig.color}`,
                    borderRadius: 1,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {scenarioConfig.label}
                </Typography>
              </Box>
            ))
          )}
        </Box>

        {/* ============================================================
            GRÁFICO
        ============================================================ */}
        <Box sx={{ width: '100%', height: height }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{
                top: 10,
                right: isMobile ? 10 : 30,
                left: isMobile ? 20 : 60,
                bottom: 10,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              
              <XAxis
                dataKey="yearLabel"
                stroke={theme.palette.text.secondary}
                style={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
              />
              
              <YAxis
                stroke={theme.palette.text.secondary}
                domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
                tickFormatter={(value) => {
                  // 🔴 FIX PROBLEMA #2 e #7: Arredondar para M (milhões) ou k (milhares)
                  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}M`;
                  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
                  return value.toString();
                }}
                style={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                width={isMobile ? 55 : 80}
              />
              
              <Tooltip content={<CustomTooltip />} />
              
              {/* Linha divisória - 🔴 FIX PROBLEMA #6: Mover para ENTRE último histórico e primeira projeção */}
              <ReferenceLine
                x={firstProjectionYear.toString()}
                stroke={theme.palette.error.main}
                strokeWidth={2}
                strokeDasharray="5 5"
                label={{
                  value: 'Histórico | Projeção',
                  position: 'top',
                  fill: theme.palette.error.main,
                  fontSize: 12,
                  fontWeight: 'bold',
                }}
              />

              {/* ========================================================
                  ÁREA DE CONFIANÇA (Min/Max entre cenários)
              ======================================================== */}
              {showMultipleScenarios && showConfidenceArea && (
                <Area
                  type="monotone"
                  dataKey={`${selectedMetric}_otimista`}
                  stackId="1"
                  stroke="none"
                  fill={config.color}
                  fillOpacity={0.1}
                />
              )}

              {/* ========================================================
                  LINHAS - Histórico (sólido)
                  Usa campo principal (receita, ebitda, fcff) que existe apenas em 2020-2024
              ======================================================== */}
              <Line
                type="monotone"
                dataKey={selectedMetric}
                stroke={config.color}
                strokeWidth={3}
                dot={{ fill: config.color, stroke: '#fff', strokeWidth: 2, r: 5 }}
                connectNulls={false}
                isAnimationActive={true}
                animationDuration={800}
                animationEasing="ease-in-out"
              />

              {/* ========================================================
                  LINHAS - Projeção
              ======================================================== */}
              {!showMultipleScenarios ? (
                // Linha única tracejada (cenário Base)
                // Usa campo _base que existe em 2024-2029
                <Line
                  type="monotone"
                  dataKey={`${selectedMetric}_base`}
                  stroke={config.color}
                  strokeWidth={3}
                  strokeDasharray="8 4"
                  dot={{ fill: config.color, stroke: '#fff', strokeWidth: 2, r: 5 }}
                  connectNulls={false}
                  isAnimationActive={true}
                  animationDuration={800}
                  animationEasing="ease-in-out"
                />
              ) : (
                // Múltiplas linhas (cenários)
                // Cada linha usa seu campo específico (_pessimista, _base, _otimista)
                // Esses campos existem em 2024-2029
                <>
                  {Object.entries(SCENARIO_CONFIG).map(([key, scenarioConfig]) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={`${selectedMetric}_${key}`}
                      stroke={scenarioConfig.color}
                      strokeWidth={key === 'base' ? 3 : 2}
                      strokeDasharray={scenarioConfig.strokeDasharray}
                      dot={{ fill: scenarioConfig.color, stroke: '#fff', strokeWidth: 2, r: key === 'base' ? 5 : 4 }}
                      connectNulls={false}
                      isAnimationActive={true}
                      animationDuration={800}
                      animationEasing="ease-in-out"
                    />
                  ))}
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>

        {/* ============================================================
            ESTATÍSTICAS - 🔴 FIX PROBLEMA #1: Usar ano DINÂMICO ao invés de hardcoded "2030"
        ============================================================ */}
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'space-around',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Último Histórico ({lastHistoricalYear})
            </Typography>
            <Typography variant="body2" fontWeight="bold" color={config.color}>
              {config.format(historicalData[historicalData.length - 1]?.[selectedMetric] || 0)}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Projeção Final ({lastProjectionYear}) {/* 🔴 FIX: Dinâmico! */}
            </Typography>
            <Typography variant="body2" fontWeight="bold" color={config.color}>
              {/* 🔴 FIX PROBLEMA #1: Buscar pelo campo _base (cenário base) para projeções */}
              {config.format(
                projectedData[projectedData.length - 1]?.[`${selectedMetric}_base`] || 
                projectedData[projectedData.length - 1]?.[selectedMetric] || 
                0
              )}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Crescimento Total
            </Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
              color={
                (() => {
                  const lastHistorical = historicalData[historicalData.length - 1]?.[selectedMetric] || 1;
                  const lastProjected = projectedData[projectedData.length - 1]?.[`${selectedMetric}_base`] || 
                                       projectedData[projectedData.length - 1]?.[selectedMetric] || 0;
                  const growth = ((lastProjected / lastHistorical) - 1) * 100;
                  return growth > 0 ? 'success.main' : 'error.main';
                })()
              }
            >
              {(() => {
                const lastHistorical = historicalData[historicalData.length - 1]?.[selectedMetric] || 1;
                const lastProjected = projectedData[projectedData.length - 1]?.[`${selectedMetric}_base`] || 
                                     projectedData[projectedData.length - 1]?.[selectedMetric] || 0;
                const growth = ((lastProjected / lastHistorical) - 1) * 100;
                // 🔴 FIX PROBLEMA #1: Validar se é NaN ou Infinity
                if (isNaN(growth) || !isFinite(growth)) {
                  return '0.0%';
                }
                return `${growth > 0 ? '+' : ''}${growth.toFixed(1)}%`;
              })()}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default HistoricalVsProjectedChartV2;
